// src/components/coldcalendar/sections/ReportingFilters.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FunnelIcon, MagnifyingGlassIcon, ExclamationCircleIcon,
  ArrowPathIcon, BeakerIcon, ChevronDownIcon, XMarkIcon,
  CheckIcon, TagIcon, HashtagIcon, UserIcon, DocumentTextIcon,
} from '@heroicons/react/24/outline';

const TABLE_FILTER_CONFIGS = {
  title: {
    webinarField: { key: 'webinar_filter', label: 'Webinar', placeholder: 'All webinars', Icon: HashtagIcon, dataKey: 'webinar_names', badgeClass: 'bg-brightblue-50 text-brightblue-700 border border-brightblue-200' },
    testField:    { key: 'test_filter',    label: 'Test Title', placeholder: 'All test titles', Icon: BeakerIcon, dataKey: 'clean_test_titles', badgeClass: 'bg-teal-50 text-teal-700 border border-teal-200' },
    primaryField: { key: 'version_filter', label: 'Version', placeholder: 'All versions', Icon: TagIcon, dataKey: 'versions', badgeClass: 'bg-violet-50 text-violet-700 border border-violet-200' },
    searchConfig: { placeholder: 'Search titles…' },
  },
  description: {
    webinarField: { key: 'webinar_filter',    label: 'Webinar',     placeholder: 'All webinars',     Icon: HashtagIcon,    dataKey: 'webinar_names',          badgeClass: 'bg-brightblue-50 text-brightblue-700 border border-brightblue-200' },
    primaryField: { key: 'description_filter',label: 'Description', placeholder: 'All descriptions', Icon: DocumentTextIcon,dataKey: 'available_descriptions', badgeClass: 'bg-brand-50 text-brand-700 border border-brand-200' },
    searchConfig: { placeholder: 'Search descriptions…' },
  },
  account: {
    webinarField: { key: 'webinar_filter', label: 'Webinar', placeholder: 'All webinars',  Icon: HashtagIcon, dataKey: 'webinar_names',     badgeClass: 'bg-brightblue-50 text-brightblue-700 border border-brightblue-200' },
    primaryField: { key: 'account_filter', label: 'Account', placeholder: 'All accounts', Icon: UserIcon,    dataKey: 'available_accounts', badgeClass: 'bg-violet-50 text-violet-700 border border-violet-200' },
    searchConfig: { placeholder: 'Search accounts…' },
  },
};

const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder, label, Icon, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (opt) => onChange(selectedValues.includes(opt) ? selectedValues.filter((v) => v !== opt) : [...selectedValues, opt]);
  const displayText = selectedValues.length === 0 ? placeholder : selectedValues.length === 1 ? selectedValues[0] : selectedValues.length === options.length ? `All ${label}` : `${selectedValues.length} selected`;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => !disabled && setOpen(!open)} disabled={disabled}
        className={`w-full h-8 px-2.5 border rounded-lg text-xs bg-white text-left flex items-center justify-between gap-1.5 transition-colors disabled:bg-grey-100 ${selectedValues.length > 0 ? 'border-brightblue-400 ring-1 ring-brightblue-200' : 'border-grey-300 hover:border-grey-400'}`}>
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Icon className="w-3 h-3 text-grey-400 flex-shrink-0" />
          <span className={`truncate ${selectedValues.length === 0 ? 'text-grey-400' : 'text-darkblue-500 font-medium'}`}>{displayText}</span>
        </div>
        <ChevronDownIcon className={`w-3 h-3 text-grey-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-grey-200 rounded-xl shadow-lg z-[9999] max-h-56 overflow-y-auto">
          <div className="px-3 py-2 bg-grey-50 border-b border-grey-200 flex items-center justify-between sticky top-0">
            <button onClick={() => onChange(options)} className="text-xs text-brightblue-600 font-medium hover:text-brightblue-700">All ({options.length})</button>
            <button onClick={() => onChange([])} className="text-xs text-grey-500 hover:text-darkblue-500">Clear</button>
          </div>
          {options.length === 0
            ? <div className="p-3 text-xs text-grey-400 text-center">No options</div>
            : options.map((opt) => {
                const selected = selectedValues.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggle(opt)} className="w-full px-3 py-1.5 text-left text-xs hover:bg-grey-50 flex items-center justify-between gap-2">
                    <span className={`truncate ${selected ? 'font-semibold text-darkblue-500' : 'text-darkblue-500'}`}>{opt}</span>
                    {selected && <CheckIcon className="w-3 h-3 text-brightblue-600 flex-shrink-0" />}
                  </button>
                );
              })}
        </div>
      )}
    </div>
  );
};

const PerformanceFilters = ({ filters, onFiltersChange, loading, dataInfo, filterOptions, tableType = 'title', multiSelect = true }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const searchInputRef = useRef(null);
  const panelRef = useRef(null);
  const config = TABLE_FILTER_CONFIGS[tableType];

  const getDefaultFilters = useCallback(() => {
    if (!config) return { start_date: '', end_date: '', search_term: '' };
    return {
      start_date: '', end_date: '', search_term: '',
      webinar_filter: multiSelect ? [] : 'all',
      ...(config.primaryField && { [config.primaryField.key]: multiSelect ? [] : 'all' }),
      ...(tableType === 'title' && config.testField && { [config.testField.key]: multiSelect ? [] : 'all' }),
    };
  }, [tableType, multiSelect, config]);

  const currentFilters = { ...getDefaultFilters(), ...filters };
  const [localSearch, setLocalSearch] = useState(currentFilters.search_term || '');

  const handleChange = useCallback((key, value) => { onFiltersChange?.({ ...currentFilters, [key]: value }); }, [currentFilters, onFiltersChange]);
  const clearAll = useCallback(() => { onFiltersChange?.(getDefaultFilters()); setLocalSearch(''); }, [onFiltersChange, getDefaultFilters]);

  useEffect(() => { setLocalSearch(currentFilters.search_term || ''); }, [currentFilters.search_term]);
  useEffect(() => {
    const h = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setPanelOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  if (!config) return null;

  const webinarOpts = filterOptions?.[config.webinarField.dataKey] || [];
  const testOpts    = tableType === 'title' && config.testField ? filterOptions?.[config.testField.dataKey] || [] : [];
  const primaryOpts = config.primaryField ? filterOptions?.[config.primaryField.dataKey] || [] : [];
  const hasPending  = localSearch !== currentFilters.search_term;

  const activeCount = [
    currentFilters.webinar_filter?.length,
    tableType === 'title' && config.testField && currentFilters[config.testField.key]?.length,
    config.primaryField && currentFilters[config.primaryField.key]?.length,
    currentFilters.start_date,
    currentFilters.end_date,
    currentFilters.search_term,
  ].filter(Boolean).length;

  const chips = [
    ...(currentFilters.webinar_filter?.map?.((v) => ({ key: `w-${v}`, label: v, badgeClass: config.webinarField.badgeClass, onRemove: () => handleChange('webinar_filter', currentFilters.webinar_filter.filter((i) => i !== v)) })) || []),
    ...(tableType === 'title' && config.testField ? (currentFilters[config.testField.key]?.map?.((v) => ({ key: `t-${v}`, label: v.length > 20 ? `${v.slice(0,20)}…` : v, badgeClass: config.testField.badgeClass, onRemove: () => handleChange(config.testField.key, currentFilters[config.testField.key].filter((i) => i !== v)) })) || []) : []),
    ...(config.primaryField ? (currentFilters[config.primaryField.key]?.map?.((v) => ({ key: `p-${v}`, label: v.length > 20 ? `${v.slice(0,20)}…` : v, badgeClass: config.primaryField.badgeClass, onRemove: () => handleChange(config.primaryField.key, currentFilters[config.primaryField.key].filter((i) => i !== v)) })) || []) : []),
    ...((currentFilters.start_date || currentFilters.end_date) ? [{ key: 'date', label: [currentFilters.start_date, currentFilters.end_date].filter(Boolean).join(' → '), badgeClass: 'bg-grey-100 text-grey-700 border border-grey-200', onRemove: () => { handleChange('start_date',''); handleChange('end_date',''); } }] : []),
    ...(currentFilters.search_term ? [{ key: 'search', label: `"${currentFilters.search_term}"`, badgeClass: 'bg-grey-100 text-grey-700 border border-grey-200', onRemove: () => { handleChange('search_term',''); setLocalSearch(''); } }] : []),
  ];

  return (
    <div className="space-y-0" ref={panelRef}>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setPanelOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${panelOpen || activeCount > 0 ? 'bg-darkblue-500 text-white border-darkblue-600' : 'bg-white text-darkblue-500 border-grey-300 hover:border-grey-400'}`}>
          <FunnelIcon className="w-3.5 h-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-brand-500 text-white leading-none">{activeCount}</span>
          )}
          <ChevronDownIcon className={`w-3 h-3 transition-transform ${panelOpen ? 'rotate-180' : ''}`} />
        </button>

        {chips.length > 0 && <div className="w-px h-5 bg-grey-200 flex-shrink-0" />}

        {chips.map((chip) => (
          <span key={chip.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chip.badgeClass}`}>
            {chip.label}
            <button type="button" onClick={chip.onRemove} className="hover:opacity-60 flex-shrink-0"><XMarkIcon className="w-3 h-3" /></button>
          </span>
        ))}

        <div className="ml-auto flex items-center gap-3">
          {dataInfo && <span className="text-xs text-grey-400">{dataInfo.totalRows || 0} rows</span>}
          {loading && <span className="flex items-center gap-1 text-xs text-grey-400"><ArrowPathIcon className="w-3 h-3 animate-spin" />Filtering…</span>}
          {activeCount > 0 && <button onClick={clearAll} className="text-xs text-brightblue-600 hover:text-brightblue-700 font-medium underline">Clear all</button>}
        </div>
      </div>

      {/* ── Filter panel ────────────────────────────────────────────────── */}
      {panelOpen && (
        <div className="bg-white border border-grey-200 rounded-xl p-4 mt-2 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">

            <div>
              <label className="block text-xs font-semibold text-darkblue-500 mb-1">Start</label>
              <input type="date" value={currentFilters.start_date} onChange={(e) => handleChange('start_date', e.target.value)} disabled={loading}
                className="w-full h-8 px-2 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white focus:outline-none focus:ring-2 focus:ring-brightblue-300 disabled:bg-grey-100" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-darkblue-500 mb-1">End</label>
              <input type="date" value={currentFilters.end_date} onChange={(e) => handleChange('end_date', e.target.value)} disabled={loading}
                className="w-full h-8 px-2 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white focus:outline-none focus:ring-2 focus:ring-brightblue-300 disabled:bg-grey-100" />
            </div>

            <div className="z-50">
              <label className="block text-xs font-semibold text-darkblue-500 mb-1">{config.webinarField.label}</label>
              <MultiSelectDropdown options={webinarOpts} selectedValues={currentFilters.webinar_filter || []} onChange={(v) => handleChange('webinar_filter', v)} placeholder={config.webinarField.placeholder} label={config.webinarField.label} Icon={config.webinarField.Icon} disabled={loading} />
            </div>

            {tableType === 'title' && config.testField && (
              <div className="z-40">
                <label className="block text-xs font-semibold text-darkblue-500 mb-1">{config.testField.label}</label>
                <MultiSelectDropdown options={testOpts} selectedValues={currentFilters[config.testField.key] || []} onChange={(v) => handleChange(config.testField.key, v)} placeholder={config.testField.placeholder} label={config.testField.label} Icon={config.testField.Icon} disabled={loading} />
              </div>
            )}

            {config.primaryField && (
              <div className="z-30">
                <label className="block text-xs font-semibold text-darkblue-500 mb-1">{config.primaryField.label}</label>
                <MultiSelectDropdown options={primaryOpts} selectedValues={currentFilters[config.primaryField.key] || []} onChange={(v) => handleChange(config.primaryField.key, v)} placeholder={config.primaryField.placeholder} label={config.primaryField.label} Icon={config.primaryField.Icon} disabled={loading} />
              </div>
            )}

            <div className="z-20">
              <label className="block text-xs font-semibold text-darkblue-500 mb-1">
                Search {hasPending && <span className="text-brightblue-600 ml-1 font-normal">↵ to apply</span>}
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-grey-400" />
                <input ref={searchInputRef} type="text" placeholder={config.searchConfig.placeholder} value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { handleChange('search_term', localSearch); setPanelOpen(false); }}}
                  disabled={loading}
                  className={`w-full h-8 pl-7 pr-7 border rounded-lg text-xs text-darkblue-500 bg-white focus:outline-none focus:ring-2 focus:ring-brightblue-300 disabled:bg-grey-100 transition-colors ${hasPending ? 'border-brightblue-300 ring-1 ring-brightblue-200' : 'border-grey-300'}`} />
                {localSearch && (
                  <button type="button" onClick={() => { setLocalSearch(''); handleChange('search_term', ''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-darkblue-500">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {!loading && webinarOpts.length === 0 && testOpts.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg mt-3">
              <ExclamationCircleIcon className="w-4 h-4 text-yellow-600 flex-shrink-0" />
              <span className="text-xs text-yellow-700">No filter options available — check connectivity.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PerformanceFilters;